// This Toast module was mostly copied from https://github.com/takurinton/toast,
// a toast implementation using @floating-ui that seems to work well.
// Maintaining most of its features just in case we find use for them later, but
// for the most part you should only need to use it like this:
//
// ```tsx
// import { ToastProvider } from './ToastProvider';
// import { Toolbar } from './Toolbar';
//
// export function Page() {
//   return (
//     <ToastProvider>
//       <Toolbar />
//     </ToastProvider>
//   );
// }
// ```
//
// Some component inside Page:
// ```tsx
// import { useToast } from './hooks';
//
// export function Toolbar() {
//   const { infoToast } = useToast();
//   return (
//    <button
//      onClick={() => {
//        infoToast('Bookmark saved!');
//      }}
//    >
//      Bookmark
//    </button>
//  );
// }
// ```

import React from 'react';
import { useInteractions } from '@floating-ui/react';
import { ToastContext } from './ToastContext';
import { useToasts } from './hooks';
import { ProviderType, ToastPlacement } from './types';
import { ToastContent } from './ToastContent';

export const getToastPosition = (placement?: ToastPlacement) => {
  if (placement == null) return {};

  const placements = placement.split('-');
  if (placements[0] === 'top' || placements[0] === 'bottom') {
    return {
      margin: '0 auto',
      top: placements[0] === 'top' ? '20px' : undefined,
      bottom: placements[0] === 'bottom' ? '20px' : undefined,
      left:
        placements[1] === undefined
          ? 0
          : placements[1] === 'start'
            ? '20px'
            : 'auto',
      right:
        placements[1] === undefined
          ? 0
          : placements[1] === 'end'
            ? '20px'
            : 'auto',
    };
  }

  return {};
};

/**
 * This context provider wraps a page or a component that needs to display toasts.
 * See hooks.ts for the `useToasts` hook that it enables.
 *
 * Usage:
 *
 * ```tsx
 * import { ToastProvider } from './ToastProvider';
 *
 * export function Page() {
 *   return (
 *     <ToastProvider>
 *       ...
 *     </ToastProvider>
 *   );
 * }
 * ```
 */
export function ToastProvider({
  placement: defaultPlacement = 'bottom',
  delay = 5000,
  autoClose = true,
  requestClose = false,
  transition = {
    duration: 300,
    initial: () => ({
      opacity: 0,
      maxHeight: 0,
    }),
    open: ({ side }) => ({
      opacity: 1,
      transform: {
        top: 'translateY(-0.5rem)',
        right: 'translateX(0.5rem)',
        bottom: 'translateY(0.5rem)',
        left: 'translateX(-0.5rem)',
      }[side],
    }),
    close: ({ side }) => ({
      opacity: 0,
      maxHeight: 0,
      transform: {
        top: 'translateY(0.5rem)',
        right: 'translateX(-0.5rem)',
        bottom: 'translateY(-0.5rem)',
        left: 'translateX(0.5rem)',
      }[side],
    }),
  },
  background,
  color,
  closeable,
  render,
  children,
}: ProviderType) {
  const context = useToasts();
  const { getFloatingProps } = useInteractions();

  // add default toast placement to placements Set
  const placements = context.placements.add(defaultPlacement);

  return (
    <ToastContext.Provider value={context}>
      {Array.from(placements).map((placement) => {
        const toasts = context.toasts
          .map((toast) => {
            if (!toast.placement) {
              // if placement undefined, set default placement
              return { ...toast, placement: defaultPlacement };
            }
            return toast;
          })
          .filter((toast) => toast.placement === placement);

        return (
          <ul
            key={placement}
            aria-live="polite"
            style={{
              position: 'fixed',
              pointerEvents: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999,
              ...getToastPosition(placement),
            }}
            {...getFloatingProps({
              role: 'region',
            })}
          >
            {toasts.map((toast, index) => (
              <ToastContent
                key={toast.id}
                id={toast.id}
                placement={toast.placement ?? placement}
                autoClose={toast.autoClose ?? autoClose}
                delay={toast.delay ?? delay}
                transition={
                  toast.transition
                    ? { ...transition, ...toast.transition }
                    : transition
                }
                ref={(node) => {
                  context.listRef.current[index] = node;
                }}
                requestClose={toast.requestClose ?? requestClose}
                text={toast.text}
                background={toast.background ?? background}
                color={toast.color ?? color}
                closeable={toast.closeable ?? closeable}
                render={toast.render ?? render}
              ></ToastContent>
            ))}
          </ul>
        );
      })}
      {children}
    </ToastContext.Provider>
  );
}
